<script>
import DateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

export default {
  name: 'BroadcastScheduleModal',
  components: {
    DateTimePicker,
    ConfirmationModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    recipientCount: {
      type: Number,
      default: 0,
    },
    inboxCount: {
      type: Number,
      default: 0,
    },
    availableLabels: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'confirm', 'create-label', 'show-add-label-modal'],
  data() {
    return {
      scheduledAt: null,
      isSubmitting: false,
      selectedLabels: [],
      showLabelDropdown: false,
      searchQuery: '',
      isCreatingLabel: false,
      dropdownPosition: 'bottom',
      selectedLabelsToRemove: [],
      showRemoveLabelDropdown: false,
      removeSearchQuery: '',
      removeDropdownPosition: 'bottom',
      atendimentoAction: 'none', // 'none' | 'add' | 'remove'
    };
  },
  computed: {
    submitButtonText() {
      if (this.isSubmitting) return 'Agendando...';
      return 'Agendar';
    },
    canSubmit() {
      return this.scheduledAt && !this.isSubmitting;
    },
    atendimentoOptions() {
      return [
        { id: 'none', label: 'Não alterar', icon: 'i-lucide-minus' },
        { id: 'remove', label: 'Ativar IA', icon: 'i-lucide-bot' },
        { id: 'add', label: 'Atendimento', icon: 'i-lucide-user' },
      ];
    },
    atendimentoDescription() {
      switch (this.atendimentoAction) {
        case 'remove':
          return `A IA será ativada para ${this.recipientCount} contatos. As respostas automáticas serão habilitadas.`;
        case 'add':
          return `A IA será desativada para ${this.recipientCount} contatos. Apenas atendentes humanos responderão.`;
        default:
          return 'A configuração da IA não será alterada.';
      }
    },
    filteredLabels() {
      // Filter out "atendimento-humano" label (handled by IA Automática section)
      const labelsWithoutAtendimento = this.availableLabels.filter(
        label => label.title.toLowerCase() !== 'atendimento-humano'
      );

      if (!this.searchQuery) return labelsWithoutAtendimento;

      const query = this.searchQuery.toLowerCase();
      return labelsWithoutAtendimento.filter(label =>
        label.title.toLowerCase().includes(query)
      );
    },
    isNewLabel() {
      if (!this.searchQuery.trim()) return false;

      const query = this.searchQuery.toLowerCase().trim();
      return !this.availableLabels.some(
        label => label.title.toLowerCase() === query
      );
    },
    filteredRemoveLabels() {
      // Filter out "atendimento-humano" label (handled by IA Automática section)
      const labelsWithoutAtendimento = this.availableLabels.filter(
        label => label.title.toLowerCase() !== 'atendimento-humano'
      );

      if (!this.removeSearchQuery) return labelsWithoutAtendimento;

      const query = this.removeSearchQuery.toLowerCase();
      return labelsWithoutAtendimento.filter(label =>
        label.title.toLowerCase().includes(query)
      );
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        // When modal opens, set default to now + 1 minute
        this.scheduledAt = this.getInitialScheduleTime();
      } else {
        this.showLabelDropdown = false;
        this.searchQuery = '';
        this.showRemoveLabelDropdown = false;
        this.removeSearchQuery = '';
        this.atendimentoAction = 'none';
      }
    },
    showLabelDropdown(newValue) {
      if (!newValue) {
        this.searchQuery = '';
        return;
      }

      this.$nextTick(() => {
        this.calculateDropdownPosition();
      });
    },
    showRemoveLabelDropdown(newValue) {
      if (!newValue) {
        this.removeSearchQuery = '';
        return;
      }

      this.$nextTick(() => {
        this.calculateRemoveDropdownPosition();
      });
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    getInitialScheduleTime() {
      const date = new Date();
      // Add 1 minute to ensure it's in the future
      date.setMinutes(date.getMinutes() + 1);
      date.setMilliseconds(0);
      return date;
    },
    onClose() {
      if (this.isSubmitting) return;
      this.scheduledAt = null;
      this.showLabelDropdown = false;
      this.searchQuery = '';
      this.showRemoveLabelDropdown = false;
      this.removeSearchQuery = '';
      this.selectedLabelsToRemove = [];
      this.selectedLabels = [];
      this.atendimentoAction = 'none';
      this.$emit('close');
    },
    async onConfirm() {
      if (!this.canSubmit) return;

      // Validate date is in the future
      const now = new Date();
      if (this.scheduledAt <= now) {
        await this.$refs.confirmDialog.showConfirmation();
        return;
      }

      this.isSubmitting = true;
      try {
        await this.$emit(
          'confirm',
          this.scheduledAt,
          this.selectedLabels,
          this.selectedLabelsToRemove,
          this.atendimentoAction
        );
        this.scheduledAt = null;
        this.selectedLabels = [];
        this.selectedLabelsToRemove = [];
        this.atendimentoAction = 'none';
      } catch (error) {
        console.error('Error scheduling broadcast:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    toggleLabel(label) {
      const index = this.selectedLabels.findIndex(
        selected => selected.id === label.id
      );
      if (index === -1) {
        this.selectedLabels.push(label);
      } else {
        this.selectedLabels.splice(index, 1);
      }
    },
    isLabelSelected(label) {
      return this.selectedLabels.some(selected => selected.id === label.id);
    },
    clearSelection() {
      this.selectedLabels = [];
    },
    clearSearch() {
      this.searchQuery = '';
    },
    toggleRemoveLabel(label) {
      const index = this.selectedLabelsToRemove.findIndex(
        selected => selected.id === label.id
      );
      if (index === -1) {
        this.selectedLabelsToRemove.push(label);
      } else {
        this.selectedLabelsToRemove.splice(index, 1);
      }
    },
    isRemoveLabelSelected(label) {
      return this.selectedLabelsToRemove.some(selected => selected.id === label.id);
    },
    clearRemoveSelection() {
      this.selectedLabelsToRemove = [];
    },
    clearRemoveSearch() {
      this.removeSearchQuery = '';
    },
    toggleDropdown() {
      this.showLabelDropdown = !this.showLabelDropdown;
      if (this.showLabelDropdown) {
        this.$nextTick(() => {
          this.calculateDropdownPosition();
        });
      }
    },
    toggleRemoveDropdown() {
      this.showRemoveLabelDropdown = !this.showRemoveLabelDropdown;
      if (this.showRemoveLabelDropdown) {
        this.$nextTick(() => {
          this.calculateRemoveDropdownPosition();
        });
      }
    },
    calculateDropdownPosition() {
      const container = this.$refs.dropdownContainer;
      if (!container) return;

      const buttonRect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 400;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        this.dropdownPosition = 'top';
      } else {
        this.dropdownPosition = 'bottom';
      }
    },
    calculateRemoveDropdownPosition() {
      const container = this.$refs.removeDropdownContainer;
      if (!container) return;

      const buttonRect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 400;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        this.removeDropdownPosition = 'top';
      } else {
        this.removeDropdownPosition = 'bottom';
      }
    },
    handleClickOutside(event) {
      const addContainer = this.$refs.dropdownContainer;
      const removeContainer = this.$refs.removeDropdownContainer;

      if (this.showLabelDropdown && addContainer && !addContainer.contains(event.target)) {
        this.showLabelDropdown = false;
      }

      if (
        this.showRemoveLabelDropdown &&
        removeContainer &&
        !removeContainer.contains(event.target)
      ) {
        this.showRemoveLabelDropdown = false;
      }
    },
    async createNewLabel() {
      if (this.isCreatingLabel || !this.searchQuery.trim() || !this.isNewLabel) {
        return;
      }

      this.isCreatingLabel = true;

      const colors = [
        '#1f93ff',
        '#f97316',
        '#0ea5e9',
        '#6366f1',
        '#8b5cf6',
        '#ec4899',
        '#f43f5e',
        '#10b981',
        '#f59e0b',
        '#ef4444',
      ];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];

      const newLabel = {
        title: this.searchQuery.trim(),
        color: randomColor,
      };

      this.$emit('create-label', newLabel);

      this.searchQuery = '';
      this.isCreatingLabel = false;
    },
    showCreateLabelDialog() {
      this.showLabelDropdown = false;
      this.$emit('show-add-label-modal');
    },
  },
};
</script>

<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/20 dark:bg-n-slate-1/70 p-4 overflow-y-auto"
    >
      <div
        class="bg-n-solid-1 dark:bg-n-solid-1 rounded-xl w-full max-w-lg shadow-xl border border-n-slate-6 dark:border-n-slate-6 my-8 max-h-[90vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-300"
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between p-6 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-xl flex-shrink-0"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 rounded-lg flex items-center justify-center shadow-md"
            >
              <i class="i-lucide-clock text-white text-xl" />
            </div>
            <h3
              class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12"
            >
              Agendar disparo
            </h3>
          </div>
          <button
            class="text-n-slate-11 dark:text-n-slate-11 p-2 hover:bg-n-slate-3 dark:hover:bg-n-slate-3 hover:text-n-slate-12 dark:hover:text-n-slate-12 rounded-lg transition-all duration-200"
            @click="onClose"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>

        <!-- Modal Content -->
        <div class="p-6 flex-1 overflow-y-auto space-y-6">
          <!-- Summary card -->
      <div class="p-4 bg-n-iris-2 border border-n-iris-6 rounded-lg space-y-2">
        <div class="flex items-center justify-between">
          <span class="text-sm text-n-slate-11">Destinatários</span>
          <span class="text-lg font-semibold text-n-slate-12">
            {{ recipientCount }}
          </span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-n-slate-11">Inboxes</span>
          <span class="text-lg font-semibold text-n-slate-12">
            {{ inboxCount }}
          </span>
        </div>
      </div>

      <!-- IA Automática Quick Action -->
      <div class="p-4 bg-gradient-to-r from-n-teal-1 to-n-teal-2 border border-n-teal-6 rounded-lg space-y-3">
        <div class="flex items-start gap-2">
          <i class="i-lucide-bot text-n-teal-9 text-lg mt-0.5"></i>
          <div>
            <p class="text-sm font-medium text-n-teal-12">
              Ativar IA
            </p>
            <p class="text-xs text-n-teal-11">
              Remove ou adiciona os contatos ao Atendimento.
            </p>
          </div>
        </div>

        <!-- Segmented Control -->
        <div class="flex gap-1 p-1 bg-n-slate-3 rounded-lg">
          <button
            v-for="option in atendimentoOptions"
            :key="option.id"
            type="button"
            :class="[
              'flex-1 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2',
              atendimentoAction === option.id
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-4',
            ]"
            @click="atendimentoAction = option.id"
          >
            <i :class="option.icon" />
            <span>{{ option.label }}</span>
          </button>
        </div>

        <!-- Informative text based on selection -->
        <div
          v-if="atendimentoAction !== 'none'"
          class="p-2 rounded-md text-xs"
          :class="
            atendimentoAction === 'remove'
              ? 'bg-n-teal-3 text-n-teal-11 border border-n-teal-6'
              : 'bg-n-amber-2 text-n-amber-11 border border-n-amber-6'
          "
        >
          <i class="i-lucide-info mr-1" />
          {{ atendimentoDescription }}
        </div>
      </div>

      <!-- Label Selection -->
      <div
        class="p-4 bg-n-iris-2 border border-n-iris-6 rounded-lg space-y-3"
      >
        <div class="flex items-start gap-2">
          <i class="i-lucide-tag text-n-iris-9 text-lg mt-0.5"></i>
          <div>
            <p class="text-sm font-medium text-n-iris-12">
              Adicionar etiquetas aos contatos (opcional)
            </p>
          </div>
        </div>

        <div ref="dropdownContainer" class="relative">
          <button
            class="w-full px-4 py-3 bg-n-solid-1 border border-n-slate-6 rounded-lg text-left flex items-center justify-between hover:border-n-iris-6 transition-colors"
            @click="toggleDropdown"
          >
            <div
              v-if="selectedLabels.length > 0"
              class="flex items-center gap-2 flex-wrap"
            >
              <span class="text-n-slate-12">
                {{ selectedLabels.length }} etiqueta{{
                  selectedLabels.length > 1 ? 's' : ''
                }}
                selecionada{{ selectedLabels.length > 1 ? 's' : '' }}
              </span>
              <div class="flex gap-1">
                <span
                  v-for="label in selectedLabels.slice(0, 3)"
                  :key="label.id"
                  class="w-3 h-3 rounded-full"
                  :style="{ backgroundColor: label.color }"
                  :title="label.title"
                />
                <span
                  v-if="selectedLabels.length > 3"
                  class="text-xs text-n-slate-9"
                >
                  +{{ selectedLabels.length - 3 }}
                </span>
              </div>
            </div>
            <span v-else class="text-n-slate-9">
              Selecione as etiquetas
            </span>
            <i
              :class="
                showLabelDropdown ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              "
              class="text-n-slate-9"
            />
          </button>

          <transition name="dropdown">
            <div
              v-if="showLabelDropdown"
              class="absolute left-0 right-0 bg-n-solid-1 border border-n-slate-6 rounded-lg shadow-lg z-50"
              :class="
                dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
              "
              style="max-height: 400px"
            >
              <!-- Search Input -->
              <div class="p-3 border-b border-n-slate-6">
                <div class="relative">
                  <span
                    class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2 left-2"
                  />
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Buscar ou criar etiqueta..."
                    class="h-10 [&:not(.focus)]:!border-transparent bg-n-alpha-2 !pl-8 !pr-10 border-n-weak hover:border-n-slate-6 focus:border-n-brand block w-full reset-base text-sm !px-3 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                    @keydown.enter="isNewLabel && createNewLabel()"
                  />
                  <button
                    v-if="searchQuery"
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-11"
                    @click="clearSearch"
                  >
                    <i class="i-lucide-x text-sm" />
                  </button>
                </div>
              </div>

              <!-- Clear Selection -->
              <button
                v-if="selectedLabels.length > 0 && !searchQuery"
                class="w-full px-4 py-2 text-left hover:bg-n-slate-2 transition-colors flex items-center gap-2 text-sm border-b border-n-slate-6"
                @click="clearSelection"
              >
                <i class="i-lucide-x text-n-slate-9" />
                <span class="text-n-slate-11">Limpar seleção</span>
              </button>

              <div class="max-h-48 overflow-y-auto">
                <!-- Create New Label Option when searching -->
                <button
                  v-if="searchQuery && isNewLabel"
                  :disabled="isCreatingLabel"
                  class="w-full px-4 py-2 text-left hover:bg-n-iris-2 transition-colors flex items-center gap-2 text-sm border-b border-n-slate-6"
                  @click="createNewLabel"
                >
                  <i
                    v-if="isCreatingLabel"
                    class="i-lucide-loader animate-spin text-n-iris-9"
                  />
                  <i v-else class="i-lucide-plus text-n-iris-9" />
                  <span class="text-n-iris-11">
                    Criar etiqueta "<strong>{{ searchQuery }}</strong>"
                  </span>
                </button>

                <!-- Label Options -->
                <label
                  v-for="label in filteredLabels"
                  :key="label.id"
                  class="w-full px-4 py-2 text-left hover:bg-n-slate-2 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :checked="isLabelSelected(label)"
                    class="w-4 h-4 text-n-iris-9 bg-white border-n-slate-3 rounded focus:ring-n-iris-9 focus:ring-2"
                    @change="toggleLabel(label)"
                  />
                  <span
                    class="w-3 h-3 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: label.color }"
                  />
                  <span class="text-n-slate-12 text-sm">{{ label.title }}</span>
                </label>

                <!-- No Results Message -->
                <div
                  v-if="searchQuery && filteredLabels.length === 0 && !isNewLabel"
                  class="px-4 py-3 text-center text-sm text-n-slate-9"
                >
                  Nenhuma etiqueta encontrada
                </div>

                <!-- No Labels Message -->
                <div
                  v-if="!searchQuery && availableLabels.length === 0"
                  class="px-4 py-3 text-center text-sm text-n-slate-9"
                >
                  Nenhuma etiqueta disponível
                </div>
              </div>

              <!-- Create New Label Button - Always visible at bottom -->
              <div class="border-t border-n-slate-6">
                <button
                  class="w-full px-4 py-3 text-left hover:bg-n-iris-2 transition-colors flex items-center gap-2 text-sm"
                  @click="showCreateLabelDialog"
                >
                  <i class="i-lucide-plus-circle text-n-iris-9" />
                  <span class="text-n-iris-11 font-medium">Criar nova etiqueta</span>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Warning for Label Application -->
      <div
        v-if="selectedLabels.length > 0"
        class="bg-n-amber-2 border border-n-amber-6 rounded-lg p-3"
      >
        <div class="flex items-start gap-2">
          <i class="i-lucide-info text-n-amber-9 text-sm mt-0.5" />
          <div class="text-sm text-n-amber-11">
            <p v-if="selectedLabels.length === 1">
              A etiqueta <strong>{{ selectedLabels[0].title }}</strong> será adicionada
              a todos os {{ recipientCount }} contatos quando o disparo for enviado.
            </p>
            <div v-else>
              <p class="mb-1">
                As seguintes etiquetas serão adicionadas a todos os
                {{ recipientCount }} contatos quando o disparo for enviado:
              </p>
              <ul class="list-disc list-inside ml-2">
                <li
                  v-for="label in selectedLabels"
                  :key="label.id"
                  class="flex items-center gap-2"
                >
                  <span
                    class="w-2 h-2 rounded-full inline-block"
                    :style="{ backgroundColor: label.color }"
                  />
                  <strong>{{ label.title }}</strong>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Remove Labels Selection -->
      <div
        class="p-4 bg-n-ruby-1 border border-n-ruby-6 rounded-lg space-y-3"
      >
        <div class="flex items-start gap-2">
          <i class="i-lucide-tag-off text-n-ruby-9 text-lg mt-0.5"></i>
          <div>
            <p class="text-sm font-medium text-n-ruby-12">
              Remover etiquetas dos contatos (opcional)
            </p>
          </div>
        </div>

        <div ref="removeDropdownContainer" class="relative">
          <button
            class="w-full px-4 py-3 bg-n-solid-1 border border-n-slate-6 rounded-lg text-left flex items-center justify-between hover:border-n-ruby-6 transition-colors"
            @click="toggleRemoveDropdown"
          >
            <div
              v-if="selectedLabelsToRemove.length > 0"
              class="flex items-center gap-2 flex-wrap"
            >
              <span class="text-n-slate-12">
                {{ selectedLabelsToRemove.length }} etiqueta{{
                  selectedLabelsToRemove.length > 1 ? 's' : ''
                }}
                para remover
              </span>
              <div class="flex gap-1">
                <span
                  v-for="label in selectedLabelsToRemove.slice(0, 3)"
                  :key="label.id"
                  class="w-3 h-3 rounded-full"
                  :style="{ backgroundColor: label.color }"
                  :title="label.title"
                />
                <span
                  v-if="selectedLabelsToRemove.length > 3"
                  class="text-xs text-n-slate-9"
                >
                  +{{ selectedLabelsToRemove.length - 3 }}
                </span>
              </div>
            </div>
            <span v-else class="text-n-slate-9">
              Selecione etiquetas para remover
            </span>
            <i
              :class="
                showRemoveLabelDropdown ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              "
              class="text-n-slate-9"
            />
          </button>

          <transition name="dropdown">
            <div
              v-if="showRemoveLabelDropdown"
              class="absolute left-0 right-0 bg-n-solid-1 border border-n-slate-6 rounded-lg shadow-lg z-50"
              :class="
                removeDropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
              "
              style="max-height: 400px"
            >
              <!-- Search Input -->
              <div class="p-3 border-b border-n-slate-6">
                <div class="relative">
                  <span
                    class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2 left-2"
                  />
                  <input
                    v-model="removeSearchQuery"
                    type="text"
                    placeholder="Buscar etiqueta para remover..."
                    class="h-10 [&:not(.focus)]:!border-transparent bg-n-alpha-2 !pl-8 !pr-10 border-n-weak hover:border-n-slate-6 focus:border-n-brand block w-full reset-base text-sm !px-3 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                  />
                  <button
                    v-if="removeSearchQuery"
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-11"
                    @click="clearRemoveSearch"
                  >
                    <i class="i-lucide-x text-sm" />
                  </button>
                </div>
              </div>

              <!-- Clear Selection -->
              <button
                v-if="selectedLabelsToRemove.length > 0 && !removeSearchQuery"
                class="w-full px-4 py-2 text-left hover:bg-n-slate-2 transition-colors flex items-center gap-2 text-sm border-b border-n-slate-6"
                @click="clearRemoveSelection"
              >
                <i class="i-lucide-x text-n-slate-9" />
                <span class="text-n-slate-11">Limpar seleção</span>
              </button>

              <div class="max-h-48 overflow-y-auto">
                <!-- Label Options -->
                <label
                  v-for="label in filteredRemoveLabels"
                  :key="label.id"
                  class="w-full px-4 py-2 text-left hover:bg-n-slate-2 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :checked="isRemoveLabelSelected(label)"
                    class="w-4 h-4 text-n-ruby-9 bg-white border-n-slate-3 rounded focus:ring-n-ruby-9 focus:ring-2"
                    @change="toggleRemoveLabel(label)"
                  />
                  <span
                    class="w-3 h-3 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: label.color }"
                  />
                  <span class="text-n-slate-12 text-sm">{{ label.title }}</span>
                </label>

                <!-- No Results Message -->
                <div
                  v-if="removeSearchQuery && filteredRemoveLabels.length === 0"
                  class="px-4 py-3 text-center text-sm text-n-slate-9"
                >
                  Nenhuma etiqueta encontrada
                </div>

                <!-- No Labels Message -->
                <div
                  v-if="!removeSearchQuery && availableLabels.length === 0"
                  class="px-4 py-3 text-center text-sm text-n-slate-9"
                >
                  Nenhuma etiqueta disponível
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- DateTime Picker -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-n-slate-11">
          Data e hora do agendamento
        </label>
        <DateTimePicker
          :value="scheduledAt"
          format="DD-MM-YYYY HH:mm"
          :show-second="false"
          placeholder="Selecione data e hora"
          confirm-text="Confirmar"
          @change="scheduledAt = $event"
        />
      </div>

      <!-- Important note -->
      <div class="flex items-start gap-3 p-3 bg-n-amber-2 border border-n-amber-6 rounded-lg">
        <i class="i-lucide-info text-n-amber-9 text-lg mt-0.5"></i>
        <div class="text-sm text-n-slate-11">
          <p class="font-medium text-n-slate-12 mb-1">Importante:</p>
          <ul class="list-disc list-inside space-y-1">
            <li>O disparo será enviado automaticamente na data e hora selecionadas</li>
            <li>Certifique-se de que todos os destinatários e configurações estão corretos</li>
            <li>Você poderá cancelar o agendamento antes do horário programado</li>
          </ul>
        </div>
      </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-4 border-t border-n-slate-6">
            <button
              class="flex-1 px-4 py-3 bg-n-slate-4 hover:bg-n-slate-5 dark:bg-n-slate-4 dark:hover:bg-n-slate-5 text-n-slate-12 dark:text-n-slate-12 border border-n-slate-6 dark:border-n-slate-6 rounded-lg font-medium transition-colors duration-200"
              @click="onClose"
            >
              Cancelar
            </button>
            <button
              :disabled="!canSubmit"
              class="flex-1 px-4 py-3 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-500 dark:to-primary-600 hover:from-primary-600 hover:to-primary-700 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
              @click="onConfirm"
            >
              <i v-if="isSubmitting" class="i-lucide-loader animate-spin" />
              <i v-else class="i-lucide-clock" />
              <span>{{ submitButtonText }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>

  <!-- Warning modal for past date -->
  <ConfirmationModal
    ref="confirmDialog"
    title="Horário no passado"
    description="A data e hora de agendamento deve ser no futuro. Por favor, selecione uma data e hora posterior ao momento atual."
    confirm-label="OK"
    :cancel-label="null"
  />
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
